import { Link } from 'react-router-dom';
import { AlertTriangle, CloudOff, Cookie, KeyRound, RefreshCw } from 'lucide-react';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Seo } from '../components/Seo';

const BLOCKS: {
  Icon: typeof AlertTriangle;
  title: string;
  items: string[];
}[] = [
  {
    Icon: CloudOff,
    title: 'Verbinding en “het laadt niet”',
    items: [
      'Controleer of je wifi of mobiele data goed werkt en probeer de pagina opnieuw.',
      'Werkt het wel op een ander netwerk of een ander apparaat? Soms blokkeert een bedrijfsnetwerk of een advertentieblokker bepaalde onderdelen — probeer thuis of op je telefoon zonder blokker.',
      'Blijft het mis? Noteer het tijdstip en wat je deed, en stuur ons een bericht via contact.',
    ],
  },
  {
    Icon: Cookie,
    title: 'Waar is mijn tussenstand gebleven?',
    items: [
      'ComfortTool bewaart je scan op je eigen telefoon of computer. In een privévenster (“incognito”) kan dat verloren gaan zodra je dat venster sluit.',
      'Heb je “websitedata wissen” gebruikt? Dan start je opnieuw. Download op het resultaatscherm desnoods je gratis overzicht, zodat je de cijfers niet kwijtraakt.',
    ],
  },
  {
    Icon: KeyRound,
    title: 'Inloggen en je aankoop',
    items: [
      'Gebruik exact hetzelfde e-mailadres als bij de betaling, of volg de uitleg op de bedanktpagina na je aankoop.',
      '“Wachtwoord vergeten” werkt alleen als wachtwoord-mail voor deze site aan staat. Lukt het niet? Neem contact op, dan helpen we je verder.',
    ],
  },
  {
    Icon: RefreshCw,
    title: 'Even opfrissen',
    items: [
      'Doe een harde vernieuwing van de pagina (Windows: Ctrl+Shift+R, Mac: Cmd+Shift+R).',
      'Werk je browser bij naar de nieuwste versie — dat voorkomt vaak rare foutjes.',
    ],
  },
];

export function TroubleshootingPage() {
  return (
    <>
      <Seo
        path="/troubleshooting"
        title="Troubleshooting"
        description="Veelvoorkomende vragen: geen verbinding, tussenstand kwijt, inloggen of je aankoop koppelen — met eenvoudige stappen."
        keywords="ComfortTool troubleshooting, hulp, scan, inloggen, betaling, fout"
      />
      <div className="mx-auto max-w-3xl px-4 py-10 sm:py-14">
        <Breadcrumbs items={[{ label: 'Home', to: '/' }, { label: 'Troubleshooting' }]} />
        <header className="mt-6">
          <div className="inline-flex items-center gap-2 rounded-full ct-caution-panel px-3 py-1 text-xs font-semibold ct-caution-heading">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
            Iets werkt niet?
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-ct-text sm:text-4xl">Troubleshooting</h1>
          <p className="mt-4 text-pretty text-base leading-relaxed text-ct-muted sm:text-lg">
            Meestal helpt één van deze stappen. Blijft het frusterend?{' '}
            <Link to="/contact" className="font-semibold text-ct-accent hover:underline">
              Schrijf ons even
            </Link>{' '}
            — vermeld wat je zag en welk apparaat je gebruikte.
          </p>
        </header>

        <div className="mt-12 space-y-8">
          {BLOCKS.map(({ Icon, title, items }) => (
            <section
              key={title}
              className="rounded-2xl border border-ct-border bg-ct-surface/90 p-5 sm:p-6"
              aria-labelledby={`ts-${title.replace(/\s/g, '-')}`}
            >
              <h2
                id={`ts-${title.replace(/\s/g, '-')}`}
                className="flex items-center gap-2 text-lg font-bold text-ct-text"
              >
                <Icon className="h-5 w-5 shrink-0 text-ct-accent" aria-hidden />
                {title}
              </h2>
              <ul className="mt-4 list-inside list-disc space-y-2 text-sm leading-relaxed text-ct-muted">
                {items.map((x) => (
                  <li key={x.slice(0, 40)}>{x}</li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <section className="mt-14 rounded-2xl border border-ct-border bg-ct-elevated/40 p-6">
          <h2 className="text-base font-bold text-ct-text">Meer uitleg</h2>
          <ul className="mt-3 space-y-2 text-sm text-ct-muted">
            <li>
              <Link to="/help" className="font-medium text-ct-accent hover:underline">
                Help
              </Link>
            </li>
            <li>
              <Link to="/faq" className="font-medium text-ct-accent hover:underline">
                FAQ
              </Link>
            </li>
            <li>
              <Link to="/privacy" className="font-medium text-ct-accent hover:underline">
                Privacy
              </Link>
            </li>
          </ul>
        </section>
      </div>
    </>
  );
}
